/**
 * The protocol between the course frame and the app around it.
 *
 * Three programs meet here and none of them can import the others: the Worker
 * builds the entry document and inlines `window.MVL`; `web/public/rt/
 * lms-bridge.js` is ES5 running inside a sandboxed, opaque origin; and the
 * React page on `lms.mauvely.com` drives the 1b footer. This file is the only
 * written-down agreement among the three, so a change to it is a change to all
 * of them.
 *
 * Types only — no runtime beyond the source constant and one guard, because
 * two of the three participants cannot import TypeScript at all.
 *
 * ---
 *
 * ## Ordering rules, in the entry document
 *
 * The order these scripts appear in is load-bearing. Each rule below is a real
 * failure that the order prevents.
 *
 * 1. **`window.MVL`, then `lms-bridge.js`, then `scorm.js`.** The Worker
 *    injects the first two at Review's `<script src="scorm.js">` anchor, in
 *    that order. `scorm.js` calls `discover()` during `Scorm.init` and takes
 *    whatever API exists *at that moment*; a bridge that defined itself a tick
 *    later would be found by nothing, the runtime would fall back to
 *    `localStorage`, and progress would look like it was working while
 *    reaching no server.
 *
 * 2. **`API_1484_11` goes on `window`, synchronously.** `discover()` walks
 *    `window` first and only then up through `parent` — and it cannot walk up
 *    here anyway, because the frame is sandboxed without `allow-same-origin`
 *    and touching `window.parent` throws. So the shim is same-window by
 *    necessity, not by preference: the parent's API is unreachable across an
 *    opaque origin. Defining it as a plain object with the eight RTE methods
 *    (`Initialize`, `Terminate`, `GetValue`, `SetValue`, `Commit`,
 *    `GetLastError`, `GetErrorString`, `GetDiagnostic`) is enough; `scorm.js`
 *    calls nothing else.
 *
 * 3. **`MVR_REVIEW` before `app.js`, in browse mode only.** `refreshGate()`
 *    reads it on first render, so setting it afterwards leaves the first
 *    lesson gated. It opens every gate for a teacher previewing a draft and is
 *    the existing Compose contract, reused rather than reinvented — see
 *    `worker/lib/coursepack.ts`. A frame in browse mode sends no `commit`.
 *
 * 4. **The parent identifies the frame by window handle, never by origin.** A
 *    sandboxed frame's `event.origin` is the string `"null"`, which every
 *    sandboxed frame shares — including the nested one a CustomCode block runs
 *    in. `e.source === iframe.contentWindow` is the check, exactly as Review's
 *    `useCourseBus` does it.
 */

import type { ProgressCommitWire } from '@wire';

/** Both directions carry it, and both sides drop anything without it. A page
 *  can hold a Review frame and a Learn frame; a course can hold a frame of its
 *  own that speaks neither. */
export const BRIDGE_SOURCE = 'mauvely-lms';

export type BridgeSource = typeof BRIDGE_SOURCE;

// ── frame → app ─────────────────────────────────────────────────────────────

/**
 * One lesson, as the bridge reads it out of `#course-data`.
 *
 * The bridge does not compute these — it reports what the runtime was handed.
 * `activities` is gateable blocks, `quiz` is scoreable ones, which is the "6 q"
 * tag on the 1b rail.
 */
export interface BridgeLesson {
  id: string;
  title: string;
  index: number;
  activities: number;
  quiz: number;
}

/**
 * The frame is listening, and here is what it is showing.
 *
 * Sent once per page load, after `app.js` has rendered. The app must not send
 * a command before it arrives — the runtime's buttons do not exist yet — and
 * must handle it arriving twice, because a reload inside the frame produces a
 * second one with no navigation the parent can see.
 */
export interface ReadyMessage {
  source: BridgeSource;
  type: 'ready';
  lessons: BridgeLesson[];
  /** The lesson index on screen. `-1` on the overview. */
  lesson: number;
  /** Whether **Next** is open — every required gateable block in this lesson
   *  is done. */
  gateOpen: boolean;
  /** Whether the runtime restored a previous session or started clean. Comes
   *  from `window.MVL.entry`, echoed so the app does not have to read the
   *  frame's globals. Not currently read by `bus.ts` — the rail's initial
   *  highlight comes from `lesson` above instead. */
  entry: EntryMode;
}

/** The learner moved. Sent on every lesson change, including one the app asked
 *  for — the app updates its rail from this, never from its own command. */
export interface LessonMessage {
  source: BridgeSource;
  type: 'lesson';
  lessonId: string | null;
  index: number;
  gateOpen: boolean;
}

/** The gate opened or closed inside the current lesson — an activity answered,
 *  or a reset. This is what enables and disables **Next** in the 1b footer. */
export interface GateMessage {
  source: BridgeSource;
  type: 'gate';
  gateOpen: boolean;
  lessonId: string | null;
}

/**
 * Progress, on `Commit`, on `Terminate`, on a 60-second tick, and on `flush`.
 *
 * Extends the POST body rather than restating it: the app forwards this
 * object, minus `source` and `type`, to `POST /api/lms/courses/:id/progress`
 * unchanged. If the two shapes were written out separately they would drift,
 * and the drift would look like lost progress.
 *
 * What is deliberately **not** in it: completed lessons, activity results,
 * "passed". The frame reports the runtime's positional keys inside `suspend`,
 * the number on `#progress-text`, and seconds; the server maps those through
 * the version's `structure` and decides the rest. A course document does not
 * mark its own homework.
 */
export interface CommitMessage extends ProgressCommitWire {
  source: BridgeSource;
  type: 'commit';
}

export type FrameMessage = ReadyMessage | LessonMessage | GateMessage | CommitMessage;

// ── app → frame ─────────────────────────────────────────────────────────────

/**
 * The four things the app asks the frame to do.
 *
 * All four click the runtime's own controls (`#lesson-list` links, `#next-btn`,
 * `#prev-btn`) rather than calling into it: `openLesson` is not global, and
 * driving the real buttons means the runtime's gate, its scroll restoration
 * and its own state updates all still happen.
 *
 *   · `open`  — the rail. Refused by the runtime if the lesson is gated.
 *   · `next`  — the footer's **Next lesson**, and **Mark complete**, which is
 *               the same act with a different word on it.
 *   · `prev`  — **Previous lesson**.
 *   · `flush` — **Save and close**. The app waits up to 300 ms for the
 *               `commit` that follows, posts it, then navigates.
 */
export type AppCommand =
  | { source: BridgeSource; type: 'open'; lessonId: string }
  | { source: BridgeSource; type: 'next' }
  | { source: BridgeSource; type: 'prev' }
  | { source: BridgeSource; type: 'flush' };

// ── window.MVL, the frame's initial state ───────────────────────────────────

/** Whether the runtime restored a previous session or started clean. */
export type EntryMode = 'resume' | 'ab-initio';

/** A learner is recording progress; a teacher is looking at a draft. Browse
 *  mode opens every gate and sends no `commit`. */
export type PlayMode = 'learn' | 'browse';

/**
 * The SCORM 2004 data model elements the bridge seeds itself with.
 *
 * Only what `scorm.js` actually reads and writes. `suspend_data` is the
 * runtime's own `{l,v,c,f}` JSON — indices, never labels — and `location` is
 * the lesson index as a string, which is what `cmi.location` means to this
 * runtime and to nothing else.
 */
export interface MvlCmi {
  suspend_data: string;
  location: string;
  completion_status: 'completed' | 'incomplete' | 'not attempted' | 'unknown';
  success_status: 'passed' | 'failed' | 'unknown';
  score: { raw: number | null; min: number; max: number; scaled: number | null };
  /** SCORM 2004 timeinterval, e.g. `PT1H12M30S`. Cumulative across sessions. */
  total_time: string;
}

/**
 * `window.MVL` — what the Worker inlines into the entry document, ahead of the
 * bridge.
 *
 * `learner.name` comes from the session at build time and is **never stored**
 * (decision 1 in the plan). It is here because SCORM's `cmi.learner_name` is a
 * thing a course can ask for; it does not come back out.
 *
 * Type-imported by `worker/player.ts` when it builds this object, so the
 * inlined JSON and the bridge's expectations are one declaration.
 */
export interface MvlInitialState {
  versionId: string;
  courseId: string;
  mode: PlayMode;
  learner: { id: string; name: string };
  entry: EntryMode;
  cmi: MvlCmi;
  /** The lesson to land on, mapped from the enrolment through the version's
   *  structure by the server — the frame does not resolve it. */
  resumeLessonId: string | null;
}

declare global {
  interface Window {
    /** Present only inside the course frame. */
    MVL?: MvlInitialState;
    /** Browse mode's open-every-gate flag. Compose's own contract. */
    MVR_REVIEW?: boolean;
    /** The bridge's SCORM 2004 RTE, defined synchronously before `scorm.js`. */
    API_1484_11?: Record<string, (...args: string[]) => string>;
  }
}

// ── the one guard both sides need ───────────────────────────────────────────

/**
 * Is this `MessageEvent` a message from our own frame?
 *
 * Both halves matter and neither is optional. The window handle is identity —
 * `e.origin` is `"null"` for every sandboxed frame and proves nothing — and
 * the `source` field keeps a Review frame, or a course's own nested frame,
 * from being read as a Learn message.
 */
export function isFrameMessage(e: MessageEvent, frame: Window | null | undefined): boolean {
  if (!frame || e.source !== frame) return false;
  const data = e.data as { source?: unknown } | null;
  return !!data && data.source === BRIDGE_SOURCE;
}
